import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  RiThumbUpLine,
  RiThumbDownLine,
  RiThumbUpFill,
  RiThumbDownFill,
  RiMessage3Fill,
} from "react-icons/ri";
import ContactForm from "./ContactForm";

const BARE_URL_RE = /(?<!\]\()(?<!")(?<!')\b(https?:\/\/[^\s<>)\]]+)/g;
const CONTACT_FORM_TOKEN = "[CONTACT_FORM]";

const mdComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  h1: ({ children }) => <div className="text-xl font-bold mt-2 mb-1 text-gray-700">{children}</div>,
  h2: ({ children }) => <div className="text-lg font-bold mt-2 mb-1 text-gray-700">{children}</div>,
  h3: ({ children }) => <div className="text-[14px] font-semibold mt-1.5 mb-0.5 text-gray-700">{children}</div>,
  p: ({ children }) => <div className="mb-1 last:mb-0">{children}</div>,
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-1 space-y-0.5 marker:text-gray-400">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-1 space-y-0.5 marker:text-gray-400">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline hover:text-blue-800"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-1.5 rounded-lg border border-gray-200">
      <table className="w-full text-[10px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
  th: ({ children }) => (
    <th className="px-2 py-1.5 text-left text-[10px] font-medium text-blue-600 border-b border-gray-200">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2 py-1.5 text-[10px] text-gray-700 border-b border-gray-100">
      {children}
    </td>
  ),
};

interface Props {
  messages: ChatMessage[];
  isLoadingHistory: boolean;
  hasMoreHistory: boolean;
  isLoadingOlder: boolean;
  activeConversationId: string | null;
  ratings: RatingMap;
  suggestedPrompts: string[];
  escalationSubmitted: Set<string>;
  onFeedback: (interactionId: string, rating: "thumbs_up" | "thumbs_down") => void;
  onPromptClick: (prompt: string) => void;
  onEscalationSubmit: (
    messageId: string,
    name: string,
    email: string,
    question: string
  ) => Promise<boolean>;
  onLoadOlder: () => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

// px from the top that auto-triggers loading older messages.
const LOAD_OLDER_THRESHOLD = 80;

const ChatMessages: React.FC<Props> = ({
  messages,
  isLoadingHistory,
  hasMoreHistory,
  isLoadingOlder,
  activeConversationId,
  ratings,
  suggestedPrompts,
  escalationSubmitted,
  onFeedback,
  onPromptClick,
  onEscalationSubmit,
  onLoadOlder,
  scrollContainerRef,
}) => {
  const showWelcome = messages.length === 0 && !activeConversationId;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreHistory || isLoadingOlder) return;
    if (e.currentTarget.scrollTop < LOAD_OLDER_THRESHOLD) onLoadOlder();
  };

  const lastUserQuestionByMessageIndex = useMemo(() => {
    const map: Record<number, string> = {};
    let lastUser = "";
    messages.forEach((m, idx) => {
      if (m.sender === "user") lastUser = m.text;
      map[idx] = lastUser;
    });
    return map;
  }, [messages]);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      role="log"
      aria-live="polite"
      aria-label="Chat messages"
      className="flex-1 flex flex-col p-4 overflow-y-auto bg-gray-50/80 space-y-4"
    >
      {/* Loader stuck to the top of the panel while older messages load. */}
      {isLoadingOlder && (
        <div className="sticky top-0 z-10 flex justify-center">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-sm border border-gray-100">
            <span className="w-4 h-4 border-2 border-gray-200 border-t-[#090F21] rounded-full animate-spin" />
          </span>
        </div>
      )}
      {isLoadingHistory ? (
        <div className="flex-1 flex justify-center items-center">
          <span className="w-5 h-5 border-2 border-gray-200 border-t-[#090F21] rounded-full animate-spin" />
        </div>
      ) : showWelcome ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
          <div className="w-12 h-12 rounded-full bg-[#090F21] flex items-center justify-center text-white mb-3">
            <RiMessage3Fill size={22} />
          </div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">
            Welcome
          </h3>
          <p className="text-[11px] text-gray-500 mb-4 max-w-[260px]">
            Ask me anything about solar investments, tax benefits, material
            participation, or asset ownership.
          </p>
          <div className="w-full grid grid-cols-1 gap-2">
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => onPromptClick(prompt)}
                className="text-left px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-[11px] text-gray-700 hover:border-[#090F21]/40 hover:bg-[#090F21]/5 transition-colors shadow-sm"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {messages.map((msg, idx) => {
            if (msg.sender === "user") {
              return (
                <div
                  key={msg.id}
                  className="flex flex-col items-end gap-1 ml-auto max-w-[85%]"
                >
                  <div className="bg-[#090F21] p-3.5 rounded-2xl rounded-tr-none text-xs leading-relaxed text-white shadow-md whitespace-pre-wrap">
                    {msg.text}
                  </div>
                  <span className="text-[9px] text-gray-400 font-medium mr-1 uppercase tracking-wider">
                    You
                  </span>
                </div>
              );
            }

            const hasContactForm = msg.text.includes(CONTACT_FORM_TOKEN);
            const displayText = hasContactForm
              ? msg.text.replace(/\[CONTACT_FORM\]/g, "").trimEnd()
              : msg.text;
            const userQuestion = lastUserQuestionByMessageIndex[idx] || "";

            return (
              <div key={msg.id} className="flex flex-col gap-1 max-w-[85%]">
                <div className="bg-white p-3.5 rounded-2xl rounded-tl-none text-xs leading-relaxed text-gray-700 shadow-sm border border-gray-200 w-fit">
                  {displayText ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {displayText.replace(BARE_URL_RE, "[$1]($1)")}
                    </ReactMarkdown>
                  ) : (
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-1">
                  <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wider">
                    Assistant
                  </span>
                  {msg.interactionId && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onFeedback(msg.interactionId!, "thumbs_up")}
                        disabled={!!ratings[msg.interactionId]}
                        title="Helpful"
                        className={`transition-colors disabled:cursor-default ${
                          ratings[msg.interactionId] === "thumbs_up"
                            ? "text-green-500"
                            : "text-gray-300 hover:text-green-500"
                        }`}
                      >
                        {ratings[msg.interactionId] === "thumbs_up" ? (
                          <RiThumbUpFill size={11} />
                        ) : (
                          <RiThumbUpLine size={11} />
                        )}
                      </button>
                      <button
                        onClick={() => onFeedback(msg.interactionId!, "thumbs_down")}
                        disabled={!!ratings[msg.interactionId]}
                        title="Not helpful"
                        className={`transition-colors disabled:cursor-default ${
                          ratings[msg.interactionId] === "thumbs_down"
                            ? "text-red-500"
                            : "text-gray-300 hover:text-red-500"
                        }`}
                      >
                        {ratings[msg.interactionId] === "thumbs_down" ? (
                          <RiThumbDownFill size={11} />
                        ) : (
                          <RiThumbDownLine size={11} />
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {hasContactForm && (
                  <ContactForm
                    messageId={msg.id}
                    question={userQuestion}
                    submitted={escalationSubmitted.has(msg.id)}
                    onSubmit={onEscalationSubmit}
                  />
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ChatMessages;
